import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PanelLeftClose, PanelLeftOpen, Hand, House, User } from 'lucide-react'
import { PlusIcon } from './Icons'
import ContactFormModal from './ContactFormModal'
import FeedbackModal from './FeedbackModal'
import { useSubscription } from '../contexts/SubscriptionContext.jsx'
import './Sidebar.css'

// Contact Us — chat bubble.
function ChatIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  )
}

// Competitive-intelligence — radar sweep.
function MonitorIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19.07 4.93A10 10 0 1 0 22 12" />
      <path d="M15.5 8.5A5 5 0 1 0 17 12" />
      <circle cx="12" cy="12" r="1" />
    </svg>
  )
}

function Sidebar({ collapsed = false, onToggle }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [contactOpen, setContactOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)

  /*
   * Monitoring goes straight to setup for an account without Premium, rather than through the
   * dashboard route, which would only work that out and redirect. Unknown plan: the dashboard
   * route still decides.
   */
  const { hasMonitoring, loading: planLoading } = useSubscription()
  const openMonitoring = () => navigate(!planLoading && !hasMonitoring ? '/monitor/new' : '/monitor')

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard'
    // Covers the wall and its sub-routes, plus /patent/:id — PatentDetail is still where
    // the superseded grid's results open, and the unlinked legacy route.
    if (path === '/abandoned-patents') {
      return location.pathname.startsWith('/abandoned-patents')
        || location.pathname.startsWith('/patent/')
    }
    if (path === '/new-patent') return location.pathname.startsWith('/new-patent')
    if (path === '/ideas') return location.pathname.startsWith('/ideas') || location.pathname.startsWith('/new-idea') || location.pathname.startsWith('/edit-idea')
    if (path === '/profile') return location.pathname === '/profile'
    if (path === '/learn') return location.pathname === '/learn'
    if (path === '/monitor') return location.pathname === '/monitor'
    return false
  }

  const startNewApplication = () => {
    sessionStorage.removeItem('patentApplicationStep1')
    sessionStorage.removeItem('patentSketchData')
    sessionStorage.removeItem('patentSketches')
    sessionStorage.removeItem('patentSketchLabels')
    sessionStorage.removeItem('ideaToPatent')
    navigate('/new-patent', { state: { freshStart: true } })
  }

  // `title` only while collapsed — with the label visible a tooltip repeating it is noise.
  const tip = (label) => (collapsed ? label : undefined)

  return (
    <div className={`app-sidebar ${collapsed ? 'is-collapsed' : ''}`}>
      {/* Brand mark, with the collapse toggle opposite it. */}
      <div className="sidebar-head">
        <button className="sidebar-brand" onClick={() => navigate('/dashboard')} title={tip('AutoInvent')} aria-label="AutoInvent home">
          <img src="/icons/favicon.png" alt="AutoInvent" className="sidebar-brand-img" />
        </button>
        <button
          className="sidebar-btn sidebar-toggle"
          onClick={onToggle}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
        >
          {/* No label, unlike every row below: those name a destination, this acts on the
              sidebar itself. title/aria-label carry the meaning. */}
          {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
      </div>

      <div className="sidebar-menu">
        <button className={`sidebar-btn ${isActive('/dashboard') ? 'active' : ''}`} onClick={() => navigate('/dashboard')} title={tip('Dashboard')} aria-label="Dashboard">
          <House size={19} aria-hidden="true" />
          <span className="sidebar-label">Dashboard</span>
        </button>
        <button className={`sidebar-btn ${isActive('/abandoned-patents') ? 'active' : ''}`} onClick={() => navigate('/abandoned-patents')} title={tip('Abandoned Patents')} aria-label="Abandoned Patents">
          <span className="sidebar-icon-img sidebar-icon--patents" aria-hidden="true" />
          <span className="sidebar-label">Abandoned Patents</span>
        </button>
        <button className={`sidebar-btn add-btn ${isActive('/new-patent') ? 'active' : ''}`} onClick={startNewApplication} title={tip('New Application')} aria-label="New Application">
          <PlusIcon size={18} />
          <span className="sidebar-label">New Application</span>
        </button>
        {/*
            Shown on every plan, not only Premium.

            It used to be hidden unless entitled, which meant the one feature worth $50 a month
            more was invisible to everyone who had not already bought it. The setup screen behind
            this link explains what an agent does far better than an absent nav item does, and
            the upgrade is offered at the moment somebody actually tries to build one.
          */}
        <button className={`sidebar-btn ${isActive('/monitor') ? 'active' : ''}`} onClick={openMonitoring} title={tip('Competitive Intelligence')} aria-label="Competitive Intelligence">
          <MonitorIcon />
          <span className="sidebar-label">Monitoring</span>
        </button>
        <button className="sidebar-btn" onClick={() => window.open('https://autoinvent.com/learn', '_blank', 'noopener,noreferrer')} title={tip('Learn')} aria-label="Learn (opens autoinvent.com)">
          <span className="sidebar-icon-img sidebar-icon--learn" aria-hidden="true" />
          <span className="sidebar-label">Learn</span>
        </button>
      </div>

      {/* Bottom: the Feedback card, then Contact Us + Profile */}
      <div className="sidebar-bottom">
        <button
          className="sidebar-feedback"
          onClick={() => setFeedbackOpen(true)}
          title={tip('Feedback')}
          aria-label="Give feedback"
        >
          <Hand size={18} className="sidebar-feedback-icon" />
          <span className="sidebar-feedback-text">
            <span className="sidebar-feedback-title">Feedback</span>
            <span className="sidebar-feedback-sub">Share your thoughts, improve your experience.</span>
          </span>
        </button>

        <button className="sidebar-btn" onClick={() => setContactOpen(true)} title={tip('Contact Us')} aria-label="Contact Us">
          <ChatIcon />
          <span className="sidebar-label">Contact Us</span>
        </button>
        <button className={`sidebar-btn ${isActive('/profile') ? 'active' : ''}`} onClick={() => navigate('/profile')} title={tip('Profile')} aria-label="Profile">
          <User size={19} aria-hidden="true" />
          <span className="sidebar-label">Profile</span>
        </button>
      </div>

      <ContactFormModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />
      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  )
}

export default Sidebar
